import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

export interface DayData<T = unknown> {
  day: number;
  date: Date;
  isCurrentMonth: boolean;
  bookings: T[];
}

export interface CalendarViewProps<T = { dateOfService?: string; date?: string }> {
  bookings: T[];
  renderChip: (item: T) => React.ReactNode;
}

/**


 * @param dateStr The date string to parse.
 * @returns A valid Date object or null if no format matches.
 */
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const date = new Date(dateStr);

    if (!isNaN(date.getTime()) && date.toISOString().startsWith(dateStr)) {
      return date;
    }
  }

  // Attempt to parse DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [dayStr, monthStr, yearStr] = dateStr.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);

    const date = new Date(year, month, day);

    if (
      !isNaN(date.getTime()) &&
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateStr)) {
    const [yearStr, monthStr, dayStr] = dateStr.split("/");
    const year = parseInt(yearStr, 10) + 2000;
    const month = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    const date = new Date(year, month, day);
    if (
      !isNaN(date.getTime()) &&
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  // If no formats match, return null
  console.warn(`Could not parse date format: ${dateStr}`);
  return null;
};

const CalendarView = <T extends { dateOfService?: string; date?: string },>({
  bookings,
  renderChip,
}: CalendarViewProps<T>) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const today = new Date();

  const [currentDate, setCurrentDate] = useState(new Date());

  const getMonthData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const startOfWeekDay = firstDayOfMonth.getDay();
    const daysToSubtract = startOfWeekDay === 0 ? 6 : startOfWeekDay - 1;

    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(firstDayOfMonth.getDate() - daysToSubtract);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 41);

    const calendarDays: DayData<T>[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const day = current.getDate();
      const currentMonth = current.getMonth();

      const itemsForDay = (bookings ?? []).filter((item) => {
        const rawDate = item.dateOfService || item.date;
        if (!rawDate) return false;

        const date = parseDate(rawDate);

        if (!date) {
          return false;
        }

        return (
          date.getDate() === current.getDate() &&
          date.getMonth() === current.getMonth() &&
          date.getFullYear() === current.getFullYear()
        );
      });

      calendarDays.push({
        day,
        date: new Date(current),
        isCurrentMonth: currentMonth === month,
        bookings: itemsForDay,
      });

      current.setDate(current.getDate() + 1);
    }

    return calendarDays;
  }, [currentDate, bookings]);

  const handlePrevMonth = () => {
    const prev = new Date(currentDate);
    prev.setMonth(prev.getMonth() - 1);
    setCurrentDate(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(currentDate);
    next.setMonth(next.getMonth() + 1);
    setCurrentDate(next);
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <IconButton onClick={handlePrevMonth}>
          <ArrowBackIos fontSize="small" />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          {currentDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
        {weekDays.map((day, index) => (
          <Typography
            key={index}
            variant="body2"
            align="center"
            fontWeight="bold"
            sx={{ color: theme.palette.grey[600] }}
          >
            {day}
          </Typography>
        ))}

        {getMonthData.map((data, index) => (
          <Box
            key={index}
            sx={{
              border:
                data.date.toDateString() === today.toDateString()
                  ? "2px solid #1976d2"
                  : "1px solid #e0e0e0",
              backgroundColor:
                data.date.toDateString() === today.toDateString()
                  ? "#e3f2fd"
                  : data.isCurrentMonth
                  ? "white"
                  : "#f5f5f5",
              padding: "4px",
              borderRadius: "4px",
              minHeight: isSmallScreen ? 80 : 100,
              overflow: "hidden",
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {data.day}
            </Typography>

            {/* Render each chip via prop */}
            <Box mt={0.5} display="flex" flexDirection="column" gap={0.5}>
              {data.bookings.map((item, idx) => (
                <React.Fragment key={idx}>{renderChip(item)}</React.Fragment>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CalendarView;
